import { useEffect, useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/auth/auth-store';
import { useStaffSetupList } from '@/settings/services';
import type { StaffListItem } from '@/types/settings-api';
import { StaffAddSheet } from './staff-add-sheet';

/**
 * Onboarding wizard tab 4 — Add Staff (PROD-927, epic PROD-856).
 *
 * Flutter parity: `practice_setup_module/practice_setup/presentation/widgets/
 * staff/add_staff_widget.dart`. Lists existing staff and opens the Add
 * sub-wizard via slide-in Sheet (see `staff-add-sheet.tsx`).
 *
 * Wizard Next gate (relabelled "Finish" on this tab — last data-entry step):
 * enables when `staff.length > 0`. Mirrors Flutter `:210-213`.
 *
 * Reuses (same maximum-reuse posture as PR 4):
 *   - `useStaffSetupList(orgId)` — flat `GET /api/Staff/StaffSetupList/{orgId}`
 *     (Flutter `fetchActiveStaffList`). PROD-1016: the wizard previously
 *     reused `useStaffList` (the role-grouped `/api/Staff/StaffList` endpoint),
 *     which is the wrong endpoint for this step.
 *   - `<StaffAddSheet>` — new in this PR
 *   - `useCreateStaff` / `useRolesByType(2)` / `usePracticesList` — existing
 *
 * Edit-from-wizard is disabled (placeholder) — full per-tab edit lives at
 * `/settings/staff/{id}` post-onboarding. Same scope decision as PR 4.
 */
export interface StaffStepProps {
  enableNext: () => void;
}

export function StaffStep({ enableNext }: StaffStepProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  // StaffSetupList returns a FLAT StaffListItem[] (Flutter `fetchActiveStaffList`),
  // unlike StaffList which returns role-grouped GroupedStaffList[]. No flatten.
  const { data: staff = [], isLoading } = useStaffSetupList(orgId);

  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (staff.length > 0) {
      enableNext();
    }
  }, [staff.length, enableNext]);

  return (
    <div className="mx-auto max-w-[720px] px-8 py-10">
      <h1 className="[font:var(--type-card-title)] text-[var(--ink-body)]">
        Add Staff Members To Your Practice
      </h1>
      <p className="mt-4 [font:var(--type-body)] text-[var(--ink-meta)]">
        Add other people that should have access to your practice.
        <br />
        Fields marked with a red dot are mandatory.
      </p>

      <div className="mt-10 space-y-3">
        {isLoading ? (
          <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-10 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
            Loading staff…
          </div>
        ) : staff.length === 0 ? (
          <Button
            type="button"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Staff
          </Button>
        ) : (
          <>
            {staff.map((s) => (
              <StaffRow key={s.staffSetupId ?? s.emailId ?? Math.random()} staff={s} />
            ))}
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="block w-full rounded-card border border-dashed border-[var(--surface-line)] bg-[var(--surface-read)] px-4 py-4 text-center text-md font-semibold text-[var(--accent-primary)] transition-colors hover:bg-[var(--color-blue-100)]"
            >
              + Add Another Staff
            </button>
          </>
        )}
      </div>

      <StaffAddSheet open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}

function StaffRow({ staff }: { staff: StaffListItem }) {
  const fullName =
    `${staff.firstName ?? ''} ${staff.lastName ?? ''}`.trim() || 'Untitled staff';
  const tail = staff.isLastLogin
    ? `Last Active ${formatLastActive(staff.isLastLogin)}`
    : staff.invitationStatus
      ? ''
      : 'Pending invite';
  const metaLine = [staff.roleName ?? '', staff.emailId ?? '', tail]
    .filter((s) => s && s.length > 0)
    .join(' • ');

  return (
    <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-4 py-3 flex items-center gap-4">
      <Avatar name={fullName} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[var(--ink-body)] truncate">
          {fullName}
        </div>
        {metaLine && (
          <div className="[font:var(--type-body)] text-[var(--ink-meta)] truncate mt-1">
            {metaLine}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled
        title="Edit available from Settings → Staff after finishing onboarding"
      >
        <Pencil className="h-4 w-4 mr-1" />
        Edit
      </Button>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="h-10 w-10 rounded-full bg-[var(--color-surface-background)] text-[var(--ink-meta)] flex items-center justify-center text-size-xs font-semibold flex-shrink-0">
      {initials || '?'}
    </div>
  );
}

function formatLastActive(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
